/**
 * TransitionEdge — the React Flow port of the Rappid `merode.Transition` link
 * (phase 6d). The FSM counterpart of EdgeBase.
 *
 * Like the EDG edges, an FSM transition is a FLOATING edge drawn from precomputed
 * geometry: the path `d`, the marker id, the highlight class and the label are all
 * derived UPSTREAM in `FsmFlow.deriveEdges` (where the uimodel node ellipses are in
 * scope) and handed to the edge through `data`. This keeps the geometry
 * pixel-identical to the old Rappid paper (ellipse boundary anchoring in
 * edgeGeometry.ts) and independent of React Flow's async node measurement.
 *
 * The component paints (ported from `lib/joint/shapes.js:merode.Transition` +
 * `components/FSM/transition.js`):
 *   - the wide invisible `.merode-fsm-edge-wrap` hit path (15px, the EDG default-
 *     theme wrap, matching EdgeBase).
 *   - the visible connection `<path>`: stroke `#333333`, strokeWidth 1, rounded
 *     join. The TARGET marker is the MANY arrowhead (transition.js:
 *     `MARKER_TRANSITION_END = MANY`), swapping to the highlighted variant.
 *   - the label: the `\n`-joined method names, rendered as a multi-line block at
 *     the precomputed label point, fill #333333 14px. In INTERACTIVE mode the
 *     label is DRAGGABLE — dragging it persists a new `{ distance, offset }`
 *     labelPosition through the existing command (the Rappid label-drag gesture),
 *     following the cursor live and committing on drag end.
 *   - in INTERACTIVE mode, when the transition is SELECTED, the draggable vertex
 *     handles (FsmVertexHandles) at each persisted vertex.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { EdgeLabelRenderer, useReactFlow, type EdgeProps, type Edge } from '@xyflow/react';
import type { Pt } from './edgeGeometry';
import type { FsmHighlightClass } from './FsmNodes';
import FsmVertexHandles, { type FsmVertexDragLive } from './FsmVertexHandles';
import { vertexHandlesVisible } from './vertexEditing';

export type FsmEdgeLabel = {
  /** Stable key for React. */
  key: string;
  /** The `\n`-joined method names (each line rendered separately). */
  lines: string[];
  /** Label centre in FLOW (graph) coordinates. */
  point: Pt;
};

export type TransitionEdgeData = {
  /** Precomputed SVG path `d` (rounded connector through ellipse boundary points). */
  d: string;
  /** marker-end id (the MANY arrowhead or its highlighted variant). */
  markerEndId: string | null;
  /** Highlight class for stroke/label recolour, or null. */
  highlight: FsmHighlightClass;
  /** The transition label (method names), or null when the transition has none. */
  label: FsmEdgeLabel | null;
  /** The polyline waypoints (source point + vertices + target point) — for label drag. */
  points?: Pt[];
  /** Whether this transition is a self-loop (source === target). */
  selfLoop?: boolean;
  /**
   * The persisted vertices (or, during a live vertex drag, the transient ones).
   * Drives the draggable vertex handles when the edge is selected. Interactive only.
   */
  vertices?: Pt[];
  /** Commit a replaced vertices array (existing `updateFSMVertices`). Interactive only. */
  onChangeVertices?: (transitionId: string, vertices: Pt[]) => void;
  /** Report live vertices during a handle drag so the path follows. Interactive only. */
  onVertexDragLive?: FsmVertexDragLive;
  /** Selected bend index for keyboard deletion / visual state. */
  selectedVertexIndex?: number | null;
  /** Select a bend handle without changing the selected transition. */
  onSelectVertex?: (transitionId: string, index: number) => void;
  /** Delete a bend handle through the same flow-level vertex command. */
  onDeleteVertex?: (transitionId: string, index: number) => void;
  /**
   * Commit a new label position from a dragged point (existing
   * `updateFSMLinkLabelPosition`). Interactive only.
   */
  onChangeLabelPosition?: (transitionId: string, point: Pt) => void;
  [key: string]: unknown;
};

export type TransitionFlowEdge = Edge<TransitionEdgeData>;

// The FSM label font: JointJS set no font-family on the FSM `standard.*` label, so
// it inherited the page default. fontSize 14 / fill #333333 are the shape defaults.
const LABEL_FONT_SIZE = 14;
const LABEL_COLOR = '#333333';
// The orange selection accent ($accent / #ffac2c). On highlight the line, the
// arrowhead AND the label must recolour together (no partial highlight). The
// inline label color below wins over the SCSS `.joint-highlighted` rule, so the
// accent is applied here too rather than relying on the class.
const LABEL_HIGHLIGHT_COLOR = '#ffac2c';
const LABEL_LINE_HEIGHT = 1.2;

export default function TransitionEdge({ id, data, selected }: EdgeProps<TransitionFlowEdge>) {
  const { screenToFlowPosition } = useReactFlow();

  // Latest-prop refs for the window-level label-drag listeners (bound per drag).
  const labelCommitRef = React.useRef(data?.onChangeLabelPosition);
  labelCommitRef.current = data?.onChangeLabelPosition;
  const screenToFlowRef = React.useRef(screenToFlowPosition);
  screenToFlowRef.current = screenToFlowPosition;

  // Live label point during a drag so the label follows the cursor; commit on end.
  const [liveLabelPoint, setLiveLabelPoint] = React.useState<Pt | null>(null);

  const handleLabelPointerDown = React.useCallback(
    (evt: React.PointerEvent) => {
      if (evt.button !== 0 || !labelCommitRef.current) return;
      evt.stopPropagation();
      evt.preventDefault();

      const onMove = (moveEvt: PointerEvent) => {
        const flow = screenToFlowRef.current({ x: moveEvt.clientX, y: moveEvt.clientY });
        setLiveLabelPoint({ x: flow.x, y: flow.y });
      };
      const onUp = (upEvt: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        const flow = screenToFlowRef.current({ x: upEvt.clientX, y: upEvt.clientY });
        setLiveLabelPoint(null);
        if (labelCommitRef.current) labelCommitRef.current(id, { x: flow.x, y: flow.y });
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [id]
  );

  if (!data) return null;
  const {
    d,
    markerEndId,
    highlight,
    label,
    selfLoop,
    vertices,
    onChangeVertices,
    onVertexDragLive,
    selectedVertexIndex,
    onSelectVertex,
    onDeleteVertex,
    onChangeLabelPosition,
  } = data;

  const groupClass = ['merode-fsm-edge', highlight].filter(Boolean).join(' ');
  const labelDraggable = onChangeLabelPosition != null;
  const labelPoint = liveLabelPoint ?? (label ? label.point : null);

  // Vertex-editing handles: only on the SELECTED transition in interactive mode.
  // Inserting a vertex also selects the transition so a new knikpunt is visible
  // even if it was not selected.
  const showVertexHandles =
    vertices != null &&
    vertexHandlesVisible({ selected: selected === true, vertices }) &&
    onChangeVertices != null &&
    onVertexDragLive != null;

  return (
    <>
      <g className={groupClass}>
        {/*
          Invisible wide hit-area — port of the JointJS `.connection-wrap`.
          `pointerEvents: 'stroke'` catches hits near the 1px line so a
          click/hover still lands, matching the old paper.
        */}
        <path
          className="merode-fsm-edge-wrap"
          d={d}
          fill="none"
          stroke="transparent"
          strokeWidth={15}
          strokeLinecap="round"
          style={{ pointerEvents: 'stroke' }}
        />
        <path
          className="merode-fsm-edge-path"
          d={d}
          fill="none"
          stroke="#333333"
          strokeWidth={1}
          strokeLinejoin="round"
          markerEnd={markerEndId ? `url(#${markerEndId})` : undefined}
        />
      </g>
      {label && label.lines.length > 0 && labelPoint ? (
        <EdgeLabelRenderer>
          <div
            key={label.key}
            className={[
              'merode-fsm-edge-label',
              labelDraggable ? 'nodrag nopan' : '',
              highlight,
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelPoint.x}px, ${labelPoint.y}px)`,
              fontSize: LABEL_FONT_SIZE,
              lineHeight: LABEL_LINE_HEIGHT,
              color: highlight ? LABEL_HIGHLIGHT_COLOR : LABEL_COLOR,
              textAlign: 'center',
              pointerEvents: labelDraggable ? 'all' : 'none',
              cursor: labelDraggable ? 'move' : 'default',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
            onPointerDown={labelDraggable ? handleLabelPointerDown : undefined}
          >
            {label.lines.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </EdgeLabelRenderer>
      ) : null}
      {showVertexHandles ? (
        <FsmVertexHandles
          edgeId={id}
          selfLoop={selfLoop === true}
          vertices={vertices}
          onChangeVertices={onChangeVertices}
          onVertexDragLive={onVertexDragLive}
          selectedIndex={selectedVertexIndex ?? null}
          onSelectVertex={onSelectVertex}
          onDeleteVertex={onDeleteVertex}
        />
      ) : null}
    </>
  );
}
